import { t } from '@lingui/macro';
import moment from 'moment';
import { useState } from 'react';

import { useAnalyticsClient } from '../../analytics';
import { Classroom, Group, StudentCertificates, TimeFilterDetail } from '../../api/queries/certificate-reporting/api-types';
import { useAppSelector } from '../../store/store';
import { DATE_DISPLAY_FORMAT_DD_MON_YYYY, DATE_FORMAT_DD_MM_YYYY } from '../../utils/consts';
import { TABS } from '../../utils/enums';
import { PAST_EXPORT_LEVELS } from './export-consts';
import { buildExportRows, buildPastExportRows } from './export-rows';

const sanitizeFileName = (name: string) => name
    .replace(/[\\/:*?"<>|]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const saveBlob = (blob: Blob, fileName: string) => {
    const href = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = href;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(href);
};

export function useCertificateExport() {
    const [isExporting, setIsExporting] = useState(false);
    const analytics = useAnalyticsClient();

    const { classroom, group, timeFilter } = useAppSelector(
        rootState => rootState.filtersContainerSlice
    );
    const { studentCertificates, pastCertificates, activeTab, isSummaryLoading } = useAppSelector(
        rootState => rootState.certificateResultsSlice
    );

    const isPastTab = activeTab === TABS.pastCertificates;

    const selectedClass = classroom.selected as Classroom;
    const selectedGroup = group.selected as Group;
    const selectedTimeFilter = timeFilter.selected as TimeFilterDetail;

    const classesInScope = selectedClass?.id === 0
        ? (classroom.data as Classroom[]).slice(1)
        : [selectedClass].filter(Boolean);

    const formatDate = (date: string) => moment(date, DATE_FORMAT_DD_MM_YYYY)
        .format(DATE_DISPLAY_FORMAT_DD_MON_YYYY);

    const studentsInScope = classesInScope.reduce((count, classroom) => {
        const groups = selectedGroup?.id
            ? classroom.groups.filter((group) => group.id === selectedGroup.id)
            : classroom.groups;

        return count + groups.reduce((total, group) => total + group.students.length, 0);
    }, 0);

    const hasPastRecords = pastCertificates.total.count > 0;

    const hasNewRecords = !isSummaryLoading
        && classesInScope.length > 0
        && studentsInScope > 0
        && (studentCertificates as StudentCertificates[]).length > 0;

    const hasRecords = isPastTab ? hasPastRecords : hasNewRecords;

    const exportReport = async () => {
        if (!hasRecords || isExporting) return;

        setIsExporting(true);

        try {
            const startDate = formatDate(selectedTimeFilter.startDate);
            const endDate = formatDate(selectedTimeFilter.endDate);

            const rows = isPastTab
                ? buildPastExportRows(pastCertificates)
                : buildExportRows({
                    classes: classesInScope,
                    selectedGroupId: selectedGroup?.id ?? 0,
                    studentCertificates: studentCertificates as StudentCertificates[]
                });

            const totalRow = rows.find((row) => row.type === 'total');

            analytics?.track('export.click', {
                certificate_tab: isPastTab ? 'past' : 'new',
                classroom_id: String(selectedClass?.id ?? ''),
                group_id: String(selectedGroup?.id ?? ''),
                date_range_id: String(selectedTimeFilter?.filterType ?? ''),
                start_date: startDate,
                end_date: endDate,
                row_count: rows.filter((row) => row.type !== 'total').length,
                total_certificates: totalRow?.total ?? 0,
                is_all_classes: selectedClass?.id === 0,
                is_all_groups: (selectedGroup?.id ?? 0) === 0
            });

            const [{ Workbook }, { createCertificateSheet }] = await Promise.all([
                import('exceljs'),
                import('./certificate-workbook')
            ]);

            const workbook = new Workbook();

            workbook.creator = 'certificate-report';
            workbook.created = new Date();

            createCertificateSheet(workbook, {
                rows,
                heading: `${selectedClass.name} | ${selectedGroup.name}`,
                dateRange: `${startDate} - ${endDate}`.toUpperCase(),
                levels: isPastTab ? PAST_EXPORT_LEVELS : undefined
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            const certificateType = isPastTab ? `_${t`certificates.past`}` : '';
            const fileName = sanitizeFileName(
                `${selectedClass.name}_${selectedGroup.name}${certificateType}__from_${startDate}_to_${endDate}`
            );

            saveBlob(blob, `${fileName}.xlsx`);
        } finally {
            setIsExporting(false);
        }
    };

    return { exportReport, isExporting, canExport: hasRecords };
}
